import {
	CheckCircleOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	InfoCircleOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Select, Spin, Tag } from "antd";
import { useMemo, useState } from "react";
import { SmsNavigation } from "../components/SmsNavigation";
import { getSmsDetalization } from "../services/sms.api";

function formatNum(n: number): string {
	const abs = Math.abs(n);
	const hasFraction = abs % 1 !== 0;
	const isSmall = abs < 1000;
	const formatted =
		hasFraction || isSmall
			? abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")
			: abs.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
	return n < 0 ? `\u2212${formatted}` : formatted;
}

function formatDate(isoStr: string): string {
	const d = new Date(isoStr);
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	const h = String(d.getHours()).padStart(2, "0");
	const min = String(d.getMinutes()).padStart(2, "0");
	return `${y}-${m}-${day} ${h}:${min}`;
}

const statusOptions = [
	{ label: "Barchasi", value: "all" },
	{ label: "Kutilmoqda", value: "pending" },
	{ label: "Yuborilgan", value: "sent" },
	{ label: "Etkazilgan", value: "delivered" },
	{ label: "Xatolik", value: "failed" },
];

export function SmsDetalizationPage() {
	const [currentPage, setCurrentPage] = useState(1);
	const [statusFilter, setStatusFilter] = useState("all");
	const pageSize = 15;

	const { data, isLoading } = useQuery({
		queryKey: ["smsDetalization", currentPage, statusFilter],
		queryFn: () =>
			getSmsDetalization({
				page: currentPage,
				limit: pageSize,
				status: statusFilter === "all" ? undefined : statusFilter,
			}),
	});

	const items = useMemo(() => data?.items ?? [], [data]);
	const total = data?.meta?.total ?? 0;
	const totalPages = Math.ceil(total / pageSize);

	const getStatusTag = (status: string) => {
		switch (status) {
			case "delivered":
				return (
					<Tag color="success" icon={<CheckCircleOutlined />}>
						Etkazildi
					</Tag>
				);
			case "sent":
				return (
					<Tag color="processing" icon={<ClockCircleOutlined />}>
						Yuborildi
					</Tag>
				);
			case "failed":
				return (
					<Tag color="error" icon={<CloseCircleOutlined />}>
						Xatolik
					</Tag>
				);
			default:
				return (
					<Tag color="default" icon={<InfoCircleOutlined />}>
						Kutilmoqda
					</Tag>
				);
		}
	};

	return (
		<div className="crm-sms-detalization">
			{/* Page Head */}
			<div className="crm-page-head" style={{ marginBottom: "16px" }}>
				<div>
					<h1>SMS detalizatsiya</h1>
					<p>Har bir xabar bo'yicha batafsil ma'lumot va narx tafsiloti</p>
				</div>
			</div>

			{/* Sub-Navigation Tabs */}
			<SmsNavigation activeTab="detalization" />

			{/* Filters */}
			<div style={{ marginBottom: "16px", display: "flex", gap: "12px", alignItems: "center" }}>
				<span style={{ fontWeight: 600, color: "var(--crm-ink-2)" }}>Status:</span>
				<Select
					value={statusFilter}
					onChange={(val) => {
						setStatusFilter(val);
						setCurrentPage(1);
					}}
					options={statusOptions}
					style={{ width: 180 }}
				/>
			</div>

			{/* Table */}
			<div className="crm-card crm-table-card crm-sms-detalization-table-card">
				<div className="crm-card-head">
					<h3>SMS detalizatsiya ro'yxati</h3>
				</div>
				<div className="crm-table-wrap">
					{isLoading ? (
						<div style={{ textAlign: "center", padding: "40px" }}>
							<Spin />
						</div>
					) : (
						<table className="crm-table">
							<thead>
								<tr>
									<th>Sana</th>
									<th>Telefon</th>
									<th>Xabar</th>
									<th>Turi</th>
									<th>Status</th>
									<th style={{ textAlign: "right" }}>Qismlar</th>
									<th style={{ textAlign: "right" }}>Narx/qism</th>
									<th style={{ textAlign: "right" }}>Jami narx</th>
								</tr>
							</thead>
							<tbody>
								{items.length === 0 ? (
									<tr>
										<td colSpan={8} style={{ textAlign: "center", padding: "30px 10px" }}>
											<span style={{ color: "var(--crm-ink-3)" }}>Ma'lumot topilmadi</span>
										</td>
									</tr>
								) : (
									items.map((item) => (
										<tr key={item.id}>
											<td style={{ whiteSpace: "nowrap" }}>{formatDate(item.createdAt)}</td>
											<td style={{ whiteSpace: "nowrap" }}>{item.phoneNumber}</td>
											<td
												style={{
													maxWidth: "250px",
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
												}}
											>
												{item.messageBody}
											</td>
											<td>
												<span
													className={`crm-badge ${item.triggerType === "auto" ? "blue" : "slate"}`}
												>
													{item.triggerType === "auto" ? "Avto" : "Qo'lda"}
												</span>
											</td>
											<td>{getStatusTag(item.status)}</td>
											<td style={{ textAlign: "right" }}>
												<span className="num">{item.partsCount}</span>
											</td>
											<td style={{ textAlign: "right" }}>
												<span className="num">{formatNum(item.pricePerPartUzs)}</span>
											</td>
											<td style={{ textAlign: "right" }}>
												<span className="num">{formatNum(item.totalPriceUzs)}</span>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					)}
				</div>
				<div className="crm-table-foot">
					<span>Jami {total} ta xabar</span>
					{totalPages > 1 && (
						<div className="crm-pager">
							<button
								type="button"
								disabled={currentPage === 1}
								onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
							>
								‹
							</button>
							{Array.from({ length: totalPages }).map((_, i) => (
								<button
									key={`page-${i + 1}`}
									className={currentPage === i + 1 ? "active" : ""}
									type="button"
									onClick={() => setCurrentPage(i + 1)}
								>
									{i + 1}
								</button>
							))}
							<button
								type="button"
								disabled={currentPage === totalPages}
								onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
							>
								›
							</button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default SmsDetalizationPage;
